import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { user, navigate, logout, calendarStatus, loadCalendarStatus, showToast, friendlyError, installState } from '../store.js';
import { installMode, promptInstall } from '../lib/install.js';
import { __ } from '../i18n.js';
import Header from '../components/Header.jsx';
import Icon from '../components/Icon.jsx';
import Modal from '../components/Modal.jsx';

const PROVIDER_LABELS = { google: 'Google', microsoft: 'Microsoft' };

/**
 * Phase 5B calendar section: only lists providers the backend actually has
 * OAuth credentials configured for (ETM_Calendar_Oauth::is_configured(),
 * surfaced via GET /calendar/status) — an admin who hasn't set up
 * wp-config.php yet means this section is simply absent rather than full of
 * buttons that would 501. See README's "Agenda-koppeling (Phase 5B)".
 */
export default function SettingsScreen() {
  const [busyProvider, setBusyProvider] = useState(null);
  const [savingDigest, setSavingDigest] = useState(false);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  installState.value; // subscribe: hide the row the moment the app is installed

  useEffect(() => {
    if (!calendarStatus.value) {
      loadCalendarStatus();
    }
  }, []);

  const handleLogout = () => {
    if (confirm(__('Weet je zeker dat je wilt uitloggen?', 'etm'))) {
      logout();
    }
  };

  /**
   * HH-3: the digest is on by default for every member; this is the
   * opt-out. The `user` signal carries `digest_enabled` (it ships with
   * auth/me via public_user()), so the toggle reflects server state on
   * first paint with no extra fetch — and is updated in place on success
   * rather than re-fetching the whole session.
   */
  async function handleDigestToggle(enabled) {
    if (savingDigest) return;
    const previous = user.value.digest_enabled !== false;
    setSavingDigest(true);
    // Applied optimistically and reverted on failure: the checkbox is a
    // controlled input, so without this it visibly snaps back to the old
    // state for the whole round trip and only then flips — which on a
    // slow connection reads as "my tap did nothing".
    user.value = { ...user.value, digest_enabled: enabled };
    try {
      await api.digest.setPreference(enabled);
      showToast(
        enabled ? __('Herinneringsmail staat aan.', 'etm') : __('Herinneringsmail staat uit.', 'etm'),
        'success'
      );
    } catch (err) {
      user.value = { ...user.value, digest_enabled: previous };
      showToast(friendlyError(err), 'error');
    } finally {
      setSavingDigest(false);
    }
  }

  async function handleConnect(provider) {
    if (busyProvider) return;
    setBusyProvider(provider);
    try {
      const res = await api.calendar.connect(provider);
      // Full-page navigation, deliberately not a fetch: the provider's own
      // consent screen has to load in the real browser location, and its
      // redirect back (ETM_Calendar_Oauth::handle_callback()) returns the
      // user to this same app URL — see store.js's consumeCalendarReturnParams().
      window.location.href = res.authorize_url;
    } catch (err) {
      showToast(friendlyError(err), 'error');
      setBusyProvider(null);
    }
  }

  async function handleDisconnect(provider) {
    if (busyProvider) return;
    setBusyProvider(provider);
    try {
      await api.calendar.disconnect(provider);
      await loadCalendarStatus();
      showToast(__('Agenda losgekoppeld.', 'etm'), 'success');
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setBusyProvider(null);
    }
  }

  const providers = calendarStatus.value;
  const configuredProviders = providers ? Object.entries(providers).filter(([, info]) => info.configured) : [];

  /**
   * On Chromium we hold a real beforeinstallprompt event and can open the
   * system dialog. Everywhere else (iOS Safari, or Chrome before the event
   * has fired) there is no API, so the honest answer is instructions.
   */
  async function handleInstallClick() {
    if (installMode() === 'prompt') {
      const outcome = await promptInstall();
      if (outcome === 'accepted') {
        showToast(__('ETM staat nu op je startscherm.', 'etm'), 'success');
        return;
      }
      if (outcome !== 'unavailable') return; // user said no; nothing to explain
    }
    setInstallHelpOpen(true);
  }

  return (
    <div>
      <Header title={__('Instellingen', 'etm')} />

      {user.value ? (
        <p class="etm-screen-subtitle">
          {__('Ingelogd als', 'etm')} {user.value.username}
        </p>
      ) : null}

      {/* Registration never verifies the address, so this is the user's
          only mirror for "waar zou een wachtwoord-reset-mail heengaan?".
          The @example.invalid suffix is the migration's placeholder for
          accounts that never had a usable address (see
          ETM_Mail::PLACEHOLDER_EMAIL_SUFFIX) — mail there can only
          bounce, so say that instead of showing a fake address. */}
      {user.value?.email ? (
        <div class="etm-form-group">
          <span class="etm-form-label">{__('E-mailadres', 'etm')}</span>
          {user.value.email.endsWith('@example.invalid') ? (
            <p class="etm-field-hint">
              {__('Voor dit account is geen geldig e-mailadres bekend. Wachtwoord-herstel via e-mail werkt daardoor niet; vraag de beheerder om dit te corrigeren.', 'etm')}
            </p>
          ) : (
            <div>
              <p class="etm-settings-email">{user.value.email}</p>
              <p class="etm-field-hint">
                {__('Wachtwoord-reset e-mails gaan naar dit adres.', 'etm')}
              </p>
            </div>
          )}
        </div>
      ) : null}

      {user.value ? (
        <div class="etm-form-group etm-checkbox-group">
          <input
            id="etm-digest-enabled"
            type="checkbox"
            checked={user.value.digest_enabled !== false}
            disabled={savingDigest}
            onChange={(e) => handleDigestToggle(e.currentTarget.checked)}
          />
          <label for="etm-digest-enabled">{__('Dagelijkse herinneringsmail', 'etm')}</label>
        </div>
      ) : null}
      {user.value ? (
        <p class="etm-field-hint">
          {__('Eén mail per dag als er iets je aandacht vraagt, bijvoorbeeld een contract dat bijna afloopt. Is er niets, dan krijg je geen mail.', 'etm')}
        </p>
      ) : null}

      {configuredProviders.length > 0 ? (
        <div class="etm-form-group">
          <span class="etm-form-label">{__('Agenda-koppeling', 'etm')}</span>
          {configuredProviders.map(([provider, info]) => (
            <div class="etm-calendar-row" key={provider}>
              <span>
                {PROVIDER_LABELS[provider] || provider}
                {info.connected ? ` — ${__('gekoppeld', 'etm')}` : ''}
              </span>
              <button
                type="button"
                class={`etm-btn etm-btn-sm ${info.connected ? 'etm-btn-danger' : 'etm-btn-secondary'}`}
                disabled={busyProvider === provider}
                onClick={() => (info.connected ? handleDisconnect(provider) : handleConnect(provider))}
              >
                {info.connected ? __('Loskoppelen', 'etm') : __('Koppelen', 'etm')}
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {/* Permanent recovery path for the install offer: unlike the
          dashboard card this ignores the "Later" snooze, because this is
          the row you point someone at over the phone. It disappears only
          once the app really is installed. */}
      {installMode() !== 'installed' ? (
        <button type="button" class="etm-btn etm-btn-secondary" onClick={handleInstallClick}>
          <Icon name="mobile_arrow_down" size={20} /> {__('App installeren', 'etm')}
        </button>
      ) : null}

      <button type="button" class="etm-btn etm-btn-secondary" onClick={() => navigate('category', { manage: true })}>
        <Icon name="category" size={20} /> {__('Categorieën', 'etm')}
      </button>

      <button type="button" class="etm-btn etm-btn-secondary" onClick={() => navigate('subscriptions')}>
        <Icon name="receipt_long" size={20} /> {__('Abonnementen', 'etm')}
      </button>

      <button type="button" class="etm-btn etm-btn-secondary" onClick={() => navigate('stats')}>
        <Icon name="bar_chart" size={20} /> {__('Statistieken', 'etm')}
      </button>

      <button type="button" class="etm-btn etm-btn-secondary" onClick={() => navigate('household')}>
        <Icon name="home" size={20} /> {__('Huishouden', 'etm')}
      </button>

      <button type="button" class="etm-btn etm-btn-danger" onClick={handleLogout}>
        {__('Uitloggen', 'etm')}
      </button>

      {installHelpOpen ? (
        <Modal title={__('App installeren', 'etm')} onClose={() => setInstallHelpOpen(false)}>
          <p class="etm-field-hint">{__('Zo zet je ETM op je startscherm:', 'etm')}</p>
          <ul class="etm-install-help">
            <li>{__('iPhone/iPad (Safari): tik onderin op Deel en kies "Zet op beginscherm".', 'etm')}</li>
            <li>{__('Android (Chrome): tik rechtsboven op de drie puntjes en kies "App installeren".', 'etm')}</li>
            <li>{__('Computer (Chrome/Edge): klik op het installatie-icoon rechts in de adresbalk.', 'etm')}</li>
          </ul>
          <button type="button" class="etm-btn etm-btn-secondary" onClick={() => setInstallHelpOpen(false)}>
            {__('Sluiten', 'etm')}
          </button>
        </Modal>
      ) : null}
    </div>
  );
}
